<template>
  <div
    v-show="!viewerFailed"
    class="autodesk-viewer-layout"
    :class="{ 'is-library-mobile-layout': libraryMobileLayout, 'is-admin-viewer': !libraryMode, 'is-admin-loading': !libraryMode && !viewerReady }"
  >
    <div
      ref="viewerContainer"
      class="autodesk-viewer"
      data-unit="autodesk-viewer"
    />
    <div
      v-if="libraryMobileLayout"
      v-show="libraryMobileToolbarVisible"
      ref="libraryToolbarHost"
      class="library-mobile-toolbar adsk-viewing-viewer"
      data-unit="library-mobile-viewer-toolbar"
    />
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue'
import type { ModelTreeNode } from './ThreeDModelTree.vue'
import { emptyModelTreeState, type AdminViewerState, type ModelTreeState } from '@/helpers/adminSnapshotState'
import type { ThreeDModelViewerCredentialsDto } from '@/interfaces/admin/threeDModel'
import {
  findPropertyValue,
  getAutodeskViewing,
  invalidateViewer,
  loadAutodeskViewerAssets,
  type AutodeskViewing,
  type ExplodeExtension,
  type BoxSelectionExtension,
  type SectionExtension,
  type ToolbarButton,
  type ViewerInstance,
  type ViewerModel,
  type ViewerOptions,
  type ViewerSelectionChangeEvent,
  type ViewerState
} from '@/helpers/autodeskViewer'
import useAutodeskViewerPartLocations, { translateToolbarControlId } from '@/composables/useAutodeskViewerPartLocations'
import useAutodeskLibraryViewer from '@/composables/useAutodeskLibraryViewer'

export type ViewerSelectedModelElement = {
  id: string
  name: string
  description: string | null
  mappedPart: boolean | null
  partNumber: string | null
}

export type ViewerSelectionPayload = {
  count: number
  directSelection: boolean
  element: ViewerSelectedModelElement | null
  propertyEnrichment?: boolean
  selectedDbIds: number[]
}

const props = defineProps<{
  getViewerCredentials?: () => Promise<ThreeDModelViewerCredentialsDto>
  libraryEnableExplode?: boolean
  libraryMobileLayout?: boolean
  libraryMode?: boolean
  libraryPartNumberProperty?: string | null
  librarySelectionRedirects?: Record<number, number>
  libraryShowAdvancedSelectionControls?: boolean
}>()

const emit = defineEmits<{
  (event: 'ready'): void
  (event: 'modelTreeLoaded', nodes: ModelTreeNode[]): void
  (event: 'error'): void
  (event: 'cameraChange'): void
  (event: 'partLocationChange'): void
  (event: 'selectionActivated', dbId: number): void
  (event: 'selectionChange', payload: ViewerSelectionPayload): void
}>()

const viewerContainer = ref<HTMLElement | null>(null)
const libraryToolbarHost = ref<HTMLElement | null>(null)
const viewerFailed = ref(false)
const viewerReady = ref(false)
let viewer: ViewerInstance | null = null
let resizeObserver: ResizeObserver | undefined
let isUnmounted = false
let toolbarCreatedEvent = ''
let toolbarConfiguration: Promise<void> | null = null
let cameraChangeEvent = ''
let selectionChangedEvent = ''
let selectionRequestId = 0
let defaultCameraState: ViewerState | null = null
let adminModelTreeState = emptyModelTreeState()
const {
  translateTool,
  addTranslateToolbarControl,
  setTranslateToolActive,
  updateTransformControl,
  handleSelectionChange: handleTranslateSelectionChange,
  reapplyPartLocations,
  captureDefaultTransforms,
  clearExplodeState,
  getPartLocations,
  restorePartLocations,
  resetPartLocations,
  disposeTransformControl
} = useAutodeskViewerPartLocations({
  getViewer: () => viewer,
  getLockedNodeIds: () => adminModelTreeState.lockedNodeIds,
  onPartLocationChange: () => emit('partLocationChange')
})

const hiddenToolbarControls = [
  'toolbar-panTool',
  'toolbar-zoomTool',
  'toolbar-modelStructureTool',
  'toolbar-propertiesTool',
  'toolbar-measureTool',
  'toolbar-bimWalkTool',
  'toolbar-fullscreenTool'
]

function getToolbarControl (controlId: string) {
  const toolbar = viewer?.getToolbar?.()
  return toolbar?.getControl?.(controlId) ??
    ['navTools', 'modelTools', 'settingsTools']
      .map(groupId => toolbar?.getControl?.(groupId)?.getControl?.(controlId))
      .find(Boolean)
}

function hideToolbarControl (controlId: string) {
  const control = getToolbarControl(controlId)
  control?.setVisible?.(false)
  control?.setDisplay?.('none')

  const element = viewerContainer.value?.querySelector<HTMLElement>(`#${controlId}`)
  if (element) element.style.display = 'none'
}

const {
  libraryMobileToolbarVisible,
  activateSelectionActivationTool,
  consumeDirectSelection,
  configureLibraryToolbar,
  updateLibraryToolbarButtonStates,
  setLibraryAssetReady,
  clearLibraryAssemblyOrderingFocus,
  setLibraryAssemblyOrderingFocus,
  clearLibrarySearchFocus,
  setLibrarySearchFocus
} = useAutodeskLibraryViewer({
  props,
  getViewer: () => viewer,
  viewerContainer,
  libraryToolbarHost,
  hideToolbarControl,
  onSelectionActivated: (dbId) => emit('selectionActivated', dbId)
})

function invalidateViewerBounds () {
  if (viewer?.impl) viewer.impl.canvasBoundingclientRectDirty = true
}

function handleToolbarCreated () {
  configureNativeToolbar().catch(reportViewerError)
}

function configureNativeToolbar (): Promise<void> {
  if (props.libraryMode) {
    configureLibraryToolbar()
    return Promise.resolve()
  }
  if (!toolbarConfiguration) toolbarConfiguration = configureAdminToolbar()
  return toolbarConfiguration
}

async function configureAdminToolbar () {
  // Let the SDK finish notifying existing extensions before loading new ones.
  // Loading synchronously inside toolbarCreated initializes their UI twice.
  await Promise.resolve()
  if (!viewer) return

  const activeViewer = viewer
  const extensions = await Promise.all([
    activeViewer.loadExtension?.('Autodesk.BoxSelection'),
    activeViewer.loadExtension?.('Autodesk.Explode'),
    activeViewer.loadExtension?.('Autodesk.Section'),
    activeViewer.loadExtension?.('Autodesk.DefaultTools.NavTools')
  ])
  if (isUnmounted || viewer !== activeViewer) return
  if (extensions.some(extension => !extension)) throw new Error('Required Admin viewer extension is unavailable')
  const boxSelection = extensions[0] as BoxSelectionExtension

  boxSelection.addToolbarButton(true)
  const explode = extensions[1] as ExplodeExtension
  explode.setStrategy('hierarchy')
  const explodeButton = activeViewer.getToolbar?.()?.getControl?.('modelTools')
    ?.getControl?.('toolbar-explodeTool') as ToolbarButton | undefined
  if (explodeButton) {
    explodeButton.onClick = () => {
      if (explode.isActive()) {
        const scale = explode.getScale()
        explode.deactivate()
        // The SDK resets the scale on deactivate; closing the slider should preserve it.
        explode.setScale(scale)
      } else {
        setTranslateToolActive(false)
        explode.activate()
      }
    }
  }
  addTranslateToolbarControl()
  const requiredControlIds = [
    'toolbar-explodeTool',
    translateToolbarControlId,
    'toolbar-box-selection',
    'toolbar-orbitTools',
    'toolbar-cameraSubmenuTool',
    'toolbar-sectionTool'
  ]
  if (requiredControlIds.some(id => !getToolbarControl(id))) {
    throw new Error('Required Admin viewer toolbar control is unavailable')
  }
  activeViewer.getToolbar?.()?.container?.classList.add('admin-viewer-toolbar')
  const restrictAdminToolbar = () => {
    activeViewer.getToolbar?.()?.getControl?.('settingsTools')?.setVisible?.(false)
    hiddenToolbarControls.forEach(hideToolbarControl)
    // Native responsive breakpoints hide required tools in the Snapshots panel.
    for (const controlId of requiredControlIds) {
      const control = getToolbarControl(controlId)
      control?.setVisible?.(true)
      control?.setDisplay?.('block')
    }
  }
  restrictAdminToolbar()
  // The SDK restores native button visibility on resize. Reapply the Admin scope.
  activeViewer.getToolbar?.()?.registerCustomizeToolbarCB?.(restrictAdminToolbar)
}

function handleCameraChange () {
  updateTransformControl()
  emit('cameraChange')
}

function getSelectedDbIds (event?: ViewerSelectionChangeEvent) {
  return event?.dbIdArray ??
    event?.nodeArray ??
    viewer?.getSelection?.() ??
    []
}

function getModelElement (
  dbId: number,
  onLateResult?: (element: ViewerSelectedModelElement) => void
): Promise<ViewerSelectedModelElement> {
  const instanceTree = viewer?.model?.getData?.().instanceTree
  const fallbackName = instanceTree?.getNodeName?.(dbId) ?? String(dbId)
  const fallbackElement: ViewerSelectedModelElement = {
    id: String(dbId),
    name: fallbackName,
    description: null,
    mappedPart: null,
    partNumber: null
  }

  return new Promise(resolve => {
    if (!viewer?.getProperties) {
      resolve(fallbackElement)
      return
    }

    let settled = false
    let timeout = 0
    const finish = (element: ViewerSelectedModelElement, allowLateUpdate = false) => {
      if (settled) {
        if (allowLateUpdate) onLateResult?.(element)
        return
      }
      settled = true
      window.clearTimeout(timeout)
      resolve(element)
    }
    timeout = window.setTimeout(() => finish(fallbackElement), 1000)

    viewer.getProperties(dbId, ({ name, properties = [] }) => {
      finish({
        id: String(dbId),
        name: name ?? fallbackName,
        description: findPropertyValue(properties, ['Description']),
        mappedPart: null,
        partNumber: findPropertyValue(properties, [
          props.libraryPartNumberProperty,
          'Part Number',
          'PartNumber',
          'Part No',
          'Item Number'
        ])
      }, true)
    }, () => finish(fallbackElement))
  })
}

async function emitViewerSelectionChange (
  selectedDbIds: number[],
  directSelection: boolean
) {
  const requestId = ++selectionRequestId
  if (selectedDbIds.length !== 1) {
    emit('selectionChange', {
      count: selectedDbIds.length,
      directSelection,
      element: null,
      selectedDbIds
    })
    return
  }

  const dbId = selectedDbIds[0]
  const emitCurrentSelection = (
    element: ViewerSelectedModelElement,
    propertyEnrichment = false
  ) => {
    if (requestId !== selectionRequestId) return

    emit('selectionChange', {
      count: 1,
      directSelection: propertyEnrichment ? false : directSelection,
      element,
      ...(propertyEnrichment ? { propertyEnrichment: true } : {}),
      selectedDbIds
    })
  }
  const element = await getModelElement(dbId, lateElement => {
    emitCurrentSelection(lateElement, true)
  })
  emitCurrentSelection(element)
}

function handleSelectionChange (event?: ViewerSelectionChangeEvent) {
  handleTranslateSelectionChange()
  const selectedDbIds = getSelectedDbIds(event)
  const directSelection = consumeDirectSelection(selectedDbIds)
  emitViewerSelectionChange(selectedDbIds, directSelection)
  if (props.libraryMode) updateLibraryToolbarButtonStates()
}

function reportViewerError () {
  if (isUnmounted || viewerFailed.value) return
  viewerFailed.value = true
  viewerReady.value = false
  viewer?.getToolbar?.()?.setVisible?.(false)
  emit('error')
}

function reportViewerReady () {
  if (isUnmounted || viewerFailed.value) return
  viewerReady.value = true
  emit('ready')
}

async function initializeViewer () {
  let credentials: ThreeDModelViewerCredentialsDto | undefined
  try {
    await loadAutodeskViewerAssets()
    if (isUnmounted) return
    credentials = await props.getViewerCredentials?.()
  } catch {
    if (!isUnmounted) reportViewerError()
    return
  }

  const viewing = getAutodeskViewing()
  const container = viewerContainer.value

  if (isUnmounted) return
  if (!viewing || !container) {
    reportViewerError()
    return
  }

  const options: ViewerOptions = credentials
    ? {
        env: 'AutodeskProduction2',
        api: 'streamingV2',
        getAccessToken: createAccessTokenProvider(credentials)
      }
    : { env: 'Local' }

  viewing.Initializer(options, () => {
    if (isUnmounted) return

    toolbarCreatedEvent = viewing.TOOLBAR_CREATED_EVENT
    cameraChangeEvent = viewing.CAMERA_CHANGE_EVENT ?? ''
    selectionChangedEvent = viewing.SELECTION_CHANGED_EVENT ?? ''
    viewer = new viewing.GuiViewer3D(container, {
      disabledExtensions: {
        bimwalk: true,
        layerManager: true,
        measure: true,
        modelBrowser: true,
        propertiesPanel: true,
        scalarisSimulation: true,
        viewcube: true
      },
      extensions: []
    })
    viewer.addEventListener(toolbarCreatedEvent, handleToolbarCreated)
    if (cameraChangeEvent) {
      viewer.addEventListener(cameraChangeEvent, handleCameraChange)
    }
    if (selectionChangedEvent) {
      viewer.addEventListener(selectionChangedEvent, handleSelectionChange)
    }

    if (viewer.start() !== 0) {
      removeViewerEventListeners()
      viewer = null
      reportViewerError()
      return
    }

    viewer.toolController?.registerTool(translateTool)
    // Nested editor scrolling does not reach the SDK's window scroll listener.
    window.addEventListener('scroll', invalidateViewerBounds, true)
    container.addEventListener('pointerdown', invalidateViewerBounds, true)
    container.addEventListener('pointermove', invalidateViewerBounds, true)
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => viewer?.resize?.())
      resizeObserver.observe(container)
    }
    if (props.libraryMode) activateSelectionActivationTool()
    if (credentials) {
      loadViewerDocument(viewing, credentials.urn)
    } else if (props.libraryMode) {
      reportViewerReady()
    } else {
      configureNativeToolbar().then(reportViewerReady, reportViewerError)
    }
  })
}

function removeViewerEventListeners () {
  if (!viewer) return
  if (toolbarCreatedEvent) viewer.removeEventListener(toolbarCreatedEvent, handleToolbarCreated)
  if (cameraChangeEvent) viewer.removeEventListener(cameraChangeEvent, handleCameraChange)
  if (selectionChangedEvent) viewer.removeEventListener(selectionChangedEvent, handleSelectionChange)
}

function createAccessTokenProvider (credentials: ThreeDModelViewerCredentialsDto) {
  let initialCredentials: ThreeDModelViewerCredentialsDto | undefined = credentials
  return (callback: (token: string, expiresIn: number) => void) => {
    if (isUnmounted) return
    if (initialCredentials) {
      callback(initialCredentials.accessToken, initialCredentials.expiresIn)
      initialCredentials = undefined
      return
    }

    props.getViewerCredentials?.().then((refreshed) => {
      if (!isUnmounted) callback(refreshed.accessToken, refreshed.expiresIn)
    }).catch(() => {
      if (!isUnmounted) reportViewerError()
    })
  }
}

function buildModelTree (model: ViewerModel): ModelTreeNode[] {
  const tree = model.getData?.().instanceTree
  const rootId = tree?.getRootId?.()
  if (!tree?.enumNodeChildren || !tree.getNodeName || rootId === undefined) {
    throw new Error('Model hierarchy is unavailable')
  }
  const root: ModelTreeNode = { id: String(rootId), name: tree.getNodeName(rootId) || String(rootId), children: [] }
  const pending = [root]
  const visited = new Set([rootId])
  // Iterate rather than recurse so deeply nested assemblies do not exhaust the stack.
  while (pending.length) {
    const parent = pending.pop()!
    tree.enumNodeChildren(Number(parent.id), (dbId) => {
      if (visited.has(dbId)) return
      visited.add(dbId)
      const child: ModelTreeNode = { id: String(dbId), name: tree.getNodeName!(dbId) || String(dbId), children: [] }
      parent.children!.push(child)
      pending.push(child)
    }, false)
  }
  return [root]
}

function applyAdminViewerAppearance (activeViewer: ViewerInstance) {
  activeViewer.setBackgroundColor?.(255, 255, 255, 255, 255, 255)
  activeViewer.prefs?.tag?.('no-storage', 'groundShadow')
  activeViewer.setGroundShadow?.(false)
}

function loadViewerDocument (viewing: AutodeskViewing, urn: string) {
  const onError = () => {
    if (!isUnmounted) reportViewerError()
  }
  viewing.Document.load(`urn:${urn}`, async (document) => {
    if (isUnmounted || !viewer) return
    try {
      const geometry = document.getRoot().getDefaultGeometry()
      if (!geometry) {
        onError()
        return
      }
      const activeViewer = viewer
      const model = await activeViewer.loadDocumentNode(document, geometry)
      if (isUnmounted) return
      await activeViewer.waitForLoadDone({
        geometry: true,
        propDb: !props.libraryMode,
        textures: false,
        onlyModels: [model]
      })
      if (isUnmounted || viewerFailed.value) return
      if (!props.libraryMode) applyAdminViewerAppearance(activeViewer)
      const nodes = props.libraryMode ? null : buildModelTree(model)
      activeViewer.resize?.()
      activeViewer.fitToView?.([], model, true)
      defaultCameraState = captureCameraState()
      captureDefaultTransforms()
      await configureNativeToolbar()
      if (isUnmounted || viewerFailed.value) return
      if (nodes) emit('modelTreeLoaded', nodes)
      reportViewerReady()
    } catch {
      onError()
    }
  }, onError)
}

onMounted(initializeViewer)

onBeforeUnmount(() => {
  isUnmounted = true
  window.removeEventListener('scroll', invalidateViewerBounds, true)
  viewerContainer.value?.removeEventListener('pointerdown', invalidateViewerBounds, true)
  viewerContainer.value?.removeEventListener('pointermove', invalidateViewerBounds, true)
  resizeObserver?.disconnect()
  selectionRequestId++
  removeViewerEventListeners()
  disposeTransformControl()
  viewer?.finish()
  viewer = null
})

async function clearSection () {
  if (!viewer) return

  const loadedExtension = viewer.getExtension?.('Autodesk.Section') ||
    await viewer.loadExtension?.('Autodesk.Section')
  const sectionExtension = loadedExtension as SectionExtension | undefined

  // Clear both active analyses and saved cut planes without an active gizmo.
  sectionExtension?.setSectionFromPlane(null)
}

async function resetViewerState () {
  await resetTransientViewerState()
  applyModelTreeState(emptyModelTreeState())
  resetPartLocations()
  viewer?.setActiveNavigationTool?.('orbit')
  if (defaultCameraState) {
    viewer?.restoreState?.(defaultCameraState, { viewport: true }, true)
  } else {
    viewer?.fitToView?.([], viewer.model, true)
  }
  emit('cameraChange')
}

function captureCameraState (): ViewerState | null {
  const state = viewer?.getState?.({ viewport: true })
  const viewport = state?.viewport as Record<string, unknown> | undefined
  if (!viewport) return null
  // Exclude viewport size and extension state: neither is a persisted camera change.
  const camera = Object.fromEntries(Object.entries(viewport).filter(([key]) => key !== 'aspectRatio' && key !== 'name'))
  return JSON.parse(JSON.stringify({ viewport: camera }))
}

function getPersistedState (): AdminViewerState {
  return { camera: captureCameraState(), partLocations: getPartLocations() }
}

async function restorePersistedState (state: AdminViewerState) {
  await resetTransientViewerState()
  restorePartLocations(state.partLocations)
  if (state.camera) viewer?.restoreState?.(state.camera, { viewport: true }, true)
  invalidateViewer(viewer)
}

function applyModelTreeState (state: ModelTreeState) {
  const tree = viewer?.model?.getData?.().instanceTree
  const rootId = tree?.getRootId?.()
  const visibility = viewer?.impl?.visibilityManager
  viewer?.showAll?.()
  if (rootId !== undefined) {
    visibility?.setNodeOff(rootId, false, viewer?.model)
    viewer?.lockExplode?.([rootId], false, viewer?.model)
  }
  if (adminModelTreeState.lockedNodeIds.length) {
    viewer?.lockExplode?.(adminModelTreeState.lockedNodeIds.map(Number), false, viewer?.model)
  }
  viewer?.setGhosting?.(state.ghostedNodeIds.length > 0)
  for (const id of state.ghostedNodeIds) {
    visibility?.setVisibilityOnNode(Number(id), false, viewer?.model, true, false)
  }
  for (const id of state.hiddenNodeIds) {
    const dbId = Number(id)
    const fragments: number[] = []
    tree?.enumNodeFragments(dbId, fragId => fragments.push(fragId), false)
    visibility?.setNodeOff(dbId, true, viewer?.model, [dbId], fragments)
  }
  if (state.lockedNodeIds.length) viewer?.lockExplode?.(state.lockedNodeIds.map(Number), true, viewer?.model)
  adminModelTreeState = JSON.parse(JSON.stringify(state))
  // Changing explode locks can rebuild animation transforms. Keep saved locations.
  reapplyPartLocations()
  invalidateViewer(viewer)
}

async function resetTransientViewerState () {
  clearExplodeState()
  setTranslateToolActive(false)
  const boxSelection = viewer?.getExtension?.('Autodesk.BoxSelection') as { deactivate: () => boolean } | undefined
  boxSelection?.deactivate()
  await clearSection()
  viewer?.clearSelection?.()
  emitViewerSelectionChange([], false)
}

function selectElements (nodeIds: string[]) {
  const dbIds = nodeIds
    .map((nodeId) => Number(nodeId))
    .filter((dbId) => Number.isInteger(dbId))

  if (dbIds.length === 0) {
    viewer?.clearSelection?.()
    return
  }

  if (props.libraryMode) {
    viewer?.select?.(dbIds)
    return
  }

  // Use overlays for assembly descendants too. The SDK's default MIXED mode
  // moves their geometry into the regular highlight render pass.
  const viewing = getAutodeskViewing()
  viewer?.select?.(dbIds, viewer.model, viewing?.SelectionType?.OVERLAYED)
}

async function captureSnapshotThumbnail () {
  if (!viewer?.getScreenShot) return null

  await resetTransientViewerState()
  return new Promise<string | null>((resolve) => {
    viewer?.getScreenShot?.(320, 200, (blobUrl) => resolve(blobUrl || null))
  })
}

defineExpose({
  applyModelTreeState,
  getPersistedState,
  restorePersistedState,
  captureSnapshotThumbnail,
  clearLibraryAssemblyOrderingFocus,
  clearLibrarySearchFocus,
  resetTransientViewerState,
  resetViewerState,
  selectElements,
  setLibraryAssemblyOrderingFocus,
  setLibraryAssetReady,
  setLibrarySearchFocus
})
</script>

<style scoped>
.autodesk-viewer-layout.is-admin-loading {
  visibility: hidden;
  pointer-events: none;
}

.autodesk-viewer-layout {
  display: flex;
  height: 100%;
  min-height: 360px;
  min-width: 0;
  width: 100%;
}

.autodesk-viewer {
  flex: 1;
  min-width: 0;
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 360px;
  overflow: hidden;
  background: #f5f6f7;
}

.autodesk-viewer-layout.is-admin-viewer .autodesk-viewer {
  background: #fff;
  container-type: size;
}

.autodesk-viewer-layout.is-admin-viewer .autodesk-viewer :deep(.orbit-gizmo) {
  width: 80cqmin !important;
  height: 80cqmin !important;
  top: calc(50% - 40cqmin) !important;
  left: calc(50% - 40cqmin) !important;
}

.autodesk-viewer-layout.is-admin-viewer .autodesk-viewer :deep(.orbit-gizmo .ring) {
  border-width: 8cqmin !important;
}

/* Keep SDK control ownership intact while presenting one ordered toolbar. */
.autodesk-viewer :deep(.admin-viewer-toolbar) {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 6px;
  left: 50%;
  width: max-content;
  height: auto;
  max-width: calc(100% - 20px);
  transform: translateX(-50%);
  padding: 5px;
  border: 1px solid #939ca5;
  border-radius: 4px;
  background: #fff;
  color: #0a131c;
  box-shadow: 0 2px 6px rgb(0 0 0 / 15%);
  pointer-events: auto;
}

.autodesk-viewer :deep(.admin-viewer-toolbar > #navTools),
.autodesk-viewer :deep(.admin-viewer-toolbar > #modelTools) {
  display: contents;
  color: inherit;
}

.autodesk-viewer :deep(.admin-viewer-toolbar > #navTools > .adsk-button),
.autodesk-viewer :deep(.admin-viewer-toolbar > #modelTools > .adsk-button) {
  flex: 0 0 32px;
  box-sizing: border-box;
  width: 32px;
  height: 32px;
  margin: 0;
  padding: 5px;
  color: #0a131c;
}

.autodesk-viewer :deep(.admin-viewer-toolbar > #navTools > .adsk-button > .adsk-button-icon),
.autodesk-viewer :deep(.admin-viewer-toolbar > #modelTools > .adsk-button > .adsk-button-icon) {
  padding-top: 2px;
  font-size: 19px;
}

.autodesk-viewer :deep(.admin-viewer-toolbar .translate-tool-icon::before) {
  font-size: 20px;
}

.autodesk-viewer :deep(.admin-viewer-toolbar #toolbar-explodeTool) { order: 0; }
.autodesk-viewer :deep(.admin-viewer-toolbar #translate-tool) { order: 1; }
.autodesk-viewer :deep(.admin-viewer-toolbar #toolbar-box-selection) { order: 2; }
.autodesk-viewer :deep(.admin-viewer-toolbar #toolbar-orbitTools) { order: 3; }
.autodesk-viewer :deep(.admin-viewer-toolbar #toolbar-cameraSubmenuTool) { order: 4; }
.autodesk-viewer :deep(.admin-viewer-toolbar #toolbar-sectionTool) { order: 5; }

.autodesk-viewer :deep(.admin-viewer-toolbar > #navTools > .adsk-button:hover),
.autodesk-viewer :deep(.admin-viewer-toolbar > #modelTools > .adsk-button:hover),
.autodesk-viewer :deep(.admin-viewer-toolbar > #navTools > .adsk-button.active),
.autodesk-viewer :deep(.admin-viewer-toolbar > #modelTools > .adsk-button.active) {
  background: #d5edfc;
  color: #1689c6;
  border-color: #a8cfe5;
}

.autodesk-viewer :deep(.admin-viewer-toolbar .translate-tool-active .translate-tool-icon::before) {
  color: inherit;
  padding: 0;
}

.autodesk-viewer-layout.is-library-mobile-layout {
  flex-direction: column;
}

.autodesk-viewer-layout.is-library-mobile-layout .autodesk-viewer {
  min-height: 360px;
  touch-action: none;
}

.library-mobile-toolbar {
  align-items: center;
  background: #f5f6f7;
  display: flex;
  flex: 0 0 auto;
  justify-content: center;
  min-height: 3.25rem;
  height: auto !important;
  overflow: visible !important;
  padding: 0.25rem;
  position: relative !important;
  width: 100%;
}

.library-mobile-toolbar :deep(.adsk-toolbar) {
  bottom: auto !important;
  display: flex !important;
  flex-wrap: wrap;
  justify-content: center;
  left: auto !important;
  max-width: 100%;
  position: static !important;
  transform: none !important;
}

.library-mobile-toolbar :deep(.adsk-control-group) {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
}

.library-mobile-toolbar :deep([class*="library-toolbar-"]) {
  min-height: 44px;
  min-width: 44px;
}

.autodesk-viewer :deep(.progressbg),
.autodesk-viewer :deep(.progresslb) {
  display: none;
}

.autodesk-viewer :deep(.translate-tool-icon::before) {
  content: "\f0b2";
  font-family: "Font Awesome 5 Free";
  font-size: 25px;
  font-weight: 900;
}

.autodesk-viewer :deep(.translate-tool-active .translate-tool-icon::before) {
  color: #fff;
  padding: 4px;
}

.autodesk-viewer-layout :deep([class*="library-toolbar-"]::before) {
  font-family: "Font Awesome 5 Free";
  font-size: 20px;
  font-weight: 900;
}

.autodesk-viewer-layout :deep(.library-toolbar-home::before) {
  content: "\f015";
}

.autodesk-viewer-layout :deep(.library-toolbar-explode::before) {
  content: "\f1b2";
}

.autodesk-viewer-layout :deep(.library-toolbar-hide::before) {
  content: "\f070";
}

.autodesk-viewer-layout :deep(.library-toolbar-isolate::before) {
  content: "\f247";
}

.autodesk-viewer-layout :deep(.library-toolbar-undo::before) {
  content: "\f0e2";
}

.autodesk-viewer-layout :deep(.library-toolbar-redo::before) {
  content: "\f01e";
}

.autodesk-viewer-layout :deep(.library-toolbar-resetCamera::before) {
  content: "\f030";
}

.autodesk-viewer-layout :deep(.adsk-control-tooltip) {
  background: var(--primary) !important;
  color: var(--primary--color-invert, #fff) !important;
}

@media (pointer: coarse) {
  .autodesk-viewer-layout :deep([class*="library-toolbar-"]) {
    min-height: 44px;
    min-width: 44px;
  }
}
</style>
